// componets/ThreadSidebar.js
import React, { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const isInternalNetwork = () => {
  const hostname = window.location.hostname;
  return hostname === "testreact.home-k8s.lab";
};

const API_URL = isInternalNetwork()
  ? process.env.REACT_APP_API_URL_INTERNAL
  : process.env.REACT_APP_API_URL_EXTERNAL;

const ThreadSidebar = ({
  threads,
  activeThreadId,
  onThreadSelect,
  onCreateThread,
  onUpdateThread,
  onDeleteThread,
  isOpen,
  toggleSidebar,
}) => {
  const { token } = useAuth();
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditStart = (thread) => {
    setEditingThreadId(thread._id);
    setEditTitle(thread.title);
  };

  const handleEditSubmit = async (threadId) => {
    if (editTitle.trim()) {
      await onUpdateThread(threadId, editTitle);
      setEditingThreadId(null);
    }
  };

  const handleDeleteThread = async (threadId) => {
    // FIXME! 1）应用使用ConfirmDialog；2）应该同步清除Chat.js中Message List内容
    if (window.confirm("Are you sure you want to delete this thread?")) {
      setIsDeleting(true);
      try {
        await axios.delete(`${API_URL}/api/threads/${threadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await onDeleteThread(threadId);
      } catch (error) {
        console.error("Error deleting thread:", error);
        alert("Failed to delete thread. Please try again.");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleKeyPress = (e, threadId) => {
    if (e.key === "Enter") {
      handleEditSubmit(threadId);
    } else if (e.key === "Escape") {
      setEditingThreadId(null);
    }
  };

  return (
    <>
      {/* Sidebar Overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-gray-800 border-r border-gray-700 pt-16 md:pt-20 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 z-40`}
      >
        <div className="p-4">
          <button
            onClick={onCreateThread}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg mb-4 flex items-center justify-center gap-2 transition-colors"
            disabled={isDeleting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            New Chat
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100vh-theme(spacing.16)-theme(spacing.16))]">
          {threads.map((thread) => (
            <div
              key={thread._id}
              className={`relative group p-3 hover:bg-gray-700 transition-colors
                ${activeThreadId === thread._id ? "bg-gray-700" : ""}
                ${thread.isDeleted ? "opacity-50" : ""}`}
            >
              {editingThreadId === thread._id ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={() => handleEditSubmit(thread._id)}
                  onKeyDown={(e) => handleKeyPress(e, thread._id)}
                  className="w-full bg-gray-900 text-gray-100 px-2 py-1 rounded border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                  autoFocus
                  maxLength={50}
                />
              ) : (
                <div className="flex items-center justify-between w-full">
                  <button
                    onClick={() => {
                      onThreadSelect(thread._id);
                      if (window.innerWidth < 768) {
                        toggleSidebar();
                      }
                    }}
                    className="flex-1 text-left min-w-0"
                    disabled={isDeleting}
                  >
                    <div className="flex flex-col gap-1 min-w-0">
                      {/* 这里也添加 min-w-0 */}
                      <div className="text-sm font-medium text-gray-100 truncate max-w-[180px]">
                        {thread.title}
                      </div>
                      <span className="text-xs text-gray-400 truncate">
                        {formatDistanceToNow(
                          new Date(thread.metadata.lastMessageAt),
                          {
                            addSuffix: true,
                          }
                        )}
                      </span>
                      <span className="text-xs text-gray-500">
                        {thread.metadata.messageCount} messages
                      </span>
                    </div>
                  </button>
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditStart(thread);
                      }}
                      className="text-gray-400 hover:text-blue-400 p-1"
                      title="Edit thread"
                      disabled={isDeleting}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteThread(thread._id);
                      }}
                      className="text-gray-400 hover:text-red-400 p-1"
                      title="Delete thread"
                      disabled={isDeleting}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default ThreadSidebar;
